import React from 'react';
import { ChatBubble, LoadingBar } from '@amzn/awsui-chat-components-console';
import SupportPromptGroup from '@amzn/awsui-chat-components-console/support-prompt-group';
import { Box, SpaceBetween, FileTokenGroup } from '@amzn/awsui-components-console';
import ReactMarkdown from 'react-markdown';
import ChatBubbleAvatar from './ChatBubbleAvatar';
import FeedbackActions from './FeedbackActions';
import { ChatMessage, UIState } from './types';
import { FILE_TOKEN_GROUP_I18N_STRINGS } from './constants';

interface MessageItemProps {
    message: ChatMessage;
    index: number;
    messages: ChatMessage[];
    userName: string;
    userInitial: string;
    useWebSocketControl: boolean;
    wsUiState: UIState;
    onCopyMessage: (content: string) => void;
    onSupportPromptClick: (detail: any) => void;
}

const MessageItem: React.FC<MessageItemProps> = ({
    message,
    index,
    messages,
    userName,
    userInitial,
    useWebSocketControl,
    wsUiState,
    onCopyMessage,
    onSupportPromptClick
}) => {
    const isUser = message.type === 'user';
    const prevMessage = index > 0 ? messages[index - 1] : null;
    const hideAvatar = prevMessage?.type === message.type;
    
    return (
        <div className={`message-wrapper ${isUser ? 'message-outgoing' : 'message-incoming'}`}>
            <ChatBubble
                avatar={
                    <ChatBubbleAvatar 
                        type={isUser ? 'user' : 'assistant'}
                        name={isUser ? userName : 'ADDAPT AI'}
                        initials={isUser ? userInitial : undefined}
                        loading={message.avatarLoading}
                    />
                }
                ariaLabel={`${isUser ? userName : 'ADDAPT AI'} at ${message.timestamp.toLocaleTimeString()}`}
                type={isUser ? 'outgoing' : 'incoming'}
                hideAvatar={hideAvatar}
                actions={
                message.actions === 'feedback' && !isUser ? (
                    <FeedbackActions
                        onHelpful={() => {}}
                        onNotHelpful={() =>  {}}
                        onCopy={() => onCopyMessage(typeof message.content === 'string' ? message.content : '')}
                    />
                ) : undefined
            }
        >
            <SpaceBetween size="xs">
                {typeof message.content === 'string' ? (
                    <ReactMarkdown
                        components={{
                            // Custom renderers for markdown elements
                            p: ({children}) => <div style={{ marginBottom: '8px' }}>{children}</div>,
                            strong: ({children}) => <strong>{children}</strong>,
                            em: ({children}) => <em>{children}</em>,
                            code: ({children, ...props}: any) => {
                                const isInline = !props.node?.position;
                                return isInline ? (
                                    <code style={{ 
                                        backgroundColor: '#f2f3f3', 
                                        padding: '2px 4px', 
                                        borderRadius: '3px',
                                        fontFamily: 'Monaco, Menlo, monospace',
                                        fontSize: '0.9em'
                                    }}>{children}</code>
                                ) : (
                                    <pre style={{ 
                                        backgroundColor: '#f2f3f3', 
                                        padding: '12px', 
                                        borderRadius: '4px',
                                        overflow: 'auto',
                                        fontFamily: 'Monaco, Menlo, monospace',
                                        fontSize: '0.9em'
                                    }}><code>{children}</code></pre>
                                );
                            },
                            ul: ({children}) => <ul style={{ marginLeft: '20px', marginBottom: '8px' }}>{children}</ul>,
                            ol: ({children}) => <ol style={{ marginLeft: '20px', marginBottom: '8px' }}>{children}</ol>,
                            li: ({children}) => <li style={{ marginBottom: '4px' }}>{children}</li>,
                            a: ({href, children}) => <a href={href} style={{ color: '#0073bb' }}>{children}</a>,
                            h1: ({children}) => <h1 style={{ fontSize: '1.5em', fontWeight: 'bold', marginBottom: '8px' }}>{children}</h1>,
                            h2: ({children}) => <h2 style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: '8px' }}>{children}</h2>,
                            h3: ({children}) => <h3 style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '8px' }}>{children}</h3>,
                        }}
                    >
                        {message.content}
                    </ReactMarkdown>
                ) : (
                    message.content
                )}
                {/* Show LoadingBar for assistant messages that are loading */}
                {message.avatarLoading && !isUser && (
                    <Box margin={{ top: 'xs' }}>
                        <LoadingBar variant="gen-ai" />
                    </Box>
                )}
                {message.files && message.files.length > 0 && (
                    <FileTokenGroup
                        readOnly
                        items={message.files.map(file => ({ file }))}
                        limit={3}
                        onDismiss={() => {}}
                        alignment="horizontal"
                        showFileThumbnail={true}
                        i18nStrings={FILE_TOKEN_GROUP_I18N_STRINGS}
                    />
                )}
                {/* Show chips based on mode */}
                {useWebSocketControl ? (
                    // WebSocket control mode - show both message prompts and global chips
                    <>
                        {/* Show message-specific prompts (like preview) */}
                        {message.supportPrompts && message.supportPrompts.length > 0 && (
                            <Box margin={{ top: 'xs' }}>
                                <SupportPromptGroup
                                    ariaLabel="Available actions"
                                    alignment="horizontal"
                                    onItemClick={({ detail }) => onSupportPromptClick({ detail: { id: detail.id } })}
                                    items={message.supportPrompts.map((prompt) => ({
                                        id: prompt.id,
                                        text: prompt.text
                                    }))}
                                />
                            </Box>
                        )}
                        {/* Show global chips from wsUiState on last message - hide during loading/typing states */}
                        {index === messages.length - 1 && 
                         wsUiState.chips.length > 0 && 
                         !message.avatarLoading && 
                         !message.id.startsWith('loading-') &&
                         !wsUiState.show_typing && (
                            <Box margin={{ top: 'xs' }}>
                                <SupportPromptGroup
                                    ariaLabel="Quick actions"
                                    alignment="horizontal"
                                    onItemClick={({ detail }) => onSupportPromptClick({ detail: { id: detail.id } })}
                                    items={wsUiState.chips.map((chip) => ({
                                        id: chip.id,
                                        text: chip.text,
                                        disabled: wsUiState.disable_all
                                    }))}
                                />
                            </Box>
                        )}
                    </>
                ) : (
                    // Original mode - show message.supportPrompts
                    message.supportPrompts && message.supportPrompts.length > 0 && (
                        <Box margin={{ top: 'xs' }}>
                            <SupportPromptGroup
                                ariaLabel="Suggested prompts"
                                alignment="horizontal"
                                onItemClick={({ detail }) => onSupportPromptClick({ detail: { id: detail.id } })}
                                items={message.supportPrompts.map((prompt) => ({
                                    id: prompt.id,
                                    text: prompt.text
                                }))}
                            />
                        </Box>
                    )
                )}
            </SpaceBetween>
        </ChatBubble>
        </div>
    );
};

export default MessageItem;
